import { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from 'bulma-toast';
import ChatBox from '../components/channel/ChatBox';
import ChannelConnection from '../js/connection/ChannelConnection';
import PainterroUtils from '../components/channel/PainterroUtils';
// import useUser from '../js/utils/useUser';
import useHoloXRPageTitle from '../js/utils/useHoloXRPageTitle';
import liveswitch from 'fm.liveswitch';
import ChannelToolbar from '../components/channel/ChannelToolbar';
import ChannelValidationPage from '../components/channel/ChannelValidationPage';
import SFULayoutOverlay from '../components/channel/SFULayoutOverlay';
import ChannelBanner from '../components/channel/ChannelBanner';
import { Box, Button, Columns } from 'react-bulma-components';
import ConnectionData from '../js/connection/ConnectionData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import ThumbnailPanel from "../components/channel/ThumbnailPanel";
import RootOverflowHandler from '../components/channel/RootOverflowHandler';

export default function Channel() {
    useHoloXRPageTitle('Channel');

    const connection = useRef(new ChannelConnection());
    
    const channelId = useRef(useParams().id);
    const [,setSearchParams] = useSearchParams();
    //const { user } = {};//useUser();
    const [localMedia, setLocalMedia] = useState<liveswitch.LocalMedia>();
    const [chatOpened, setChatOpened] = useState<boolean>(window.innerWidth >= 768);
    const [thumbnailsOpened, setThumbnailsOpened] = useState<boolean>(false);
    const [isMobile, setMobile] = useState<boolean>(window.innerWidth < 768);

    useEffect(() => {
        function handleResize() {
            setMobile(window.innerWidth < 768);
        }

        window.addEventListener('resize', handleResize);
        handleResize();

        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        // if (!user) {
        //     return;
        // }

        const conn = connection.current;

        function onError(error: string): void {
            toast({ message: error, type: 'is-danger', position: 'top-center', dismissible: true, duration: 20000, pauseOnHover: true });
        }

        function onScreenshot({ connectionData, videoFrameData }: { connectionData: ConnectionData, videoFrameData: VideoFrameData }): void {
            const painterroUtils = new PainterroUtils();

            painterroUtils.show(videoFrameData?.Data, (base64Image, annotations) => {
                const newFrameData: VideoFrameData = videoFrameData ?? {};
                // Remove the header since we only want to send the data
                newFrameData.Data = base64Image.split(",", 2)[1];

                const imageData: ImageAnnotationData = {
                    VideoFrameData: newFrameData,
                    Annotations: annotations
                };

                connection.current.messager.sendImage(connectionData, imageData).then(_ => {
                    toast({ message: "Image sent!", type: 'is-success', position: 'top-center' });
                }).fail(ex => onError(ex.message));
            });
        }

        if (channelId.current) {
            conn.events.addListener("onerror", onError);
            conn.events.addListener("onscreenshot", onScreenshot);
        }

        return () => {
            conn.events.removeListener("onerror", onError);
            conn.events.removeListener("onscreenshot", onScreenshot);
            conn.leaveChannel();
        }
    }, [connection, null]);

    useEffect(() => {
        if (localMedia) {
            async function openConnection() {
                setSearchParams({});
                connection.current.openConnection(localMedia!);
            }
            openConnection();
        }
    }, [setSearchParams, localMedia]);

    if (!localMedia) {
        return <ChannelValidationPage connection={connection.current} onConfirm={(lm) => setLocalMedia(lm)} />;
    }

    return (
        <>
            <RootOverflowHandler connection={connection.current} />
            <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
                <ChannelBanner connection={connection.current} />

                <Columns style={{ margin: "0 20px 20px 20px", flex: 1 }}>
                    <Columns.Column style={{ display: "flex", flexDirection: "column", height: "100%", padding: 0 }}>
                        <Box id="video" style={{ 
                            flex: 1, 
                            borderRadius: 14, 
                            padding: 0, 
                            overflow: "hidden",
                            position: "relative",
                            minHeight: "300px",
                            backgroundColor: "#000",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }} />
                        <ChannelToolbar 
                            connection={connection.current} 
                            isChatOpened={chatOpened} 
                            isMobileView={isMobile} 
                            toggleChatOpened={() => {
                                setChatOpened(!chatOpened);
                                if (!chatOpened) {
                                    setThumbnailsOpened(false); // Close thumbnails when opening chat
                                }
                            }}
                            isThumbnailsOpened={thumbnailsOpened}
                            toggleThumbnailsOpened={() => {
                                setThumbnailsOpened(!thumbnailsOpened);
                                if (!thumbnailsOpened) {
                                    setChatOpened(false); // Close chat when opening thumbnails
                                }
                            }}
                        />
                    </Columns.Column>

                    {chatOpened && !isMobile && (
                        <Columns.Column size={4} style={{ padding: "0 0 0 20px" }}>
                            <ChatBox connection={connection.current} />
                        </Columns.Column>
                    )}

                    {thumbnailsOpened && !isMobile && (
                        <Columns.Column size={4} style={{ padding: "0 0 0 20px" }}>
                            <ThumbnailPanel 
                                connections={Object.values(connection.current.sfuConnection.connections)}
                                isMobileView={isMobile}
                                onClose={() => setThumbnailsOpened(false)}
                            />
                        </Columns.Column>
                    )}

                    {chatOpened && isMobile && (
                        <div style={{ 
                            position: "absolute", 
                            top: 0, 
                            left: 0, 
                            width: "100vw", 
                            height: "100vh", 
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            backdropFilter: "blur(4px)",
                            zIndex: 22
                        }}>
                            <div style={{ 
                                position: "absolute", 
                                top: 0, 
                                right: 0, 
                                height: "100%", 
                                width: "100%",
                                backgroundColor: "#ffffff",
                                borderRadius: "12px 0 0 12px",
                                padding: "1rem"
                            }}>
                                <Button 
                                    rounded 
                                    onClick={() => setChatOpened(false)} 
                                    style={{ 
                                        position: "absolute", 
                                        top: 30, 
                                        right: 30,
                                        width: "2.5rem",
                                        height: "2.5rem",
                                        backgroundColor: "#ffffff",
                                        border: "1px solid rgba(0, 0, 0, 0.1)",
                                        color: "#000000",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: 0
                                    }}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </Button>
                                <ChatBox connection={connection.current} />
                            </div>
                        </div>
                    )}

                    {thumbnailsOpened && isMobile && (
                        <div style={{ 
                            position: "absolute", 
                            top: 0, 
                            left: 0, 
                            width: "100vw", 
                            height: "100vh", 
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            backdropFilter: "blur(4px)"
                        }}>
                            <div style={{ 
                                position: "absolute", 
                                top: 0, 
                                right: 0, 
                                height: "100%", 
                                width: "100%",
                                backgroundColor: "#ffffff",
                                borderRadius: "12px 0 0 12px",
                                padding: "1rem"
                            }}>
                                <Button 
                                    rounded 
                                    onClick={() => setThumbnailsOpened(false)} 
                                    style={{ 
                                        position: "absolute", 
                                        top: 10, 
                                        right: 10,
                                        width: "2.5rem",
                                        height: "2.5rem",
                                        backgroundColor: "#ffffff",
                                        border: "1px solid rgba(0, 0, 0, 0.1)",
                                        color: "#000000",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: 0
                                    }}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </Button>
                                <ThumbnailPanel 
                                    connections={Object.values(connection.current.sfuConnection.connections)}
                                    isMobileView={isMobile}
                                    onClose={() => setThumbnailsOpened(false)}
                                />
                            </div>
                        </div>
                    )}
                </Columns>
                <SFULayoutOverlay connection={connection.current} />
            </div>
        </>
    );
}
